import { AssetTypeAttributes } from '@/types'
import { CreateAssetTypeMutation } from './__generated__/create_mutation'
import { Mutation as CreateMutation } from './create_mutation'
import { useForm } from '@/helpers/useForm'
import { useContext, useEffect } from 'react'
import { Query as TableQuery } from './table_query'
import { useNavigate } from 'react-router'
import { Selector as TaxRateSelector } from '@/components/tax_rate/selector'
import { CurrentTenantContext } from '@/core/current_tenant_context'

interface Props {
  onSuccess: () => void
}

export const Form = ({ onSuccess }: Props) => {
  const navigate = useNavigate()

  const {
    save,
    saveSuccess,
    formBuilder,
    mutationLoading,
    mutationData,
    fieldErrors,
    attributes,
    setAttributes
  } = useForm<CreateAssetTypeMutation, AssetTypeAttributes, never, never>({
    defaultValues: {
      manufacturer: '',
      description: '',
      weight: 0,
      model: '',
      documentCategoryId: '',
      taxRateId: '',
      displayOnWebsite: false,
      costCents: 0,
      rateCents: 0,
      availableToEvents: false,
      stockCount: 0,
    },
    mutation: CreateMutation,
    resultKey: 'createAssetType',
    refetchQueries: [TableQuery]
  })
  const { currentCompany } = useContext(CurrentTenantContext)

  useEffect(() => { if (saveSuccess) onSuccess() }, [saveSuccess, onSuccess])

  useEffect(() => {
    if (!mutationData?.createAssetType?.assetType?.id) return

    navigate(`/asset-types/${mutationData?.createAssetType?.assetType?.id}`)
  }, [mutationData, navigate])

  return (
    <div className="flex flex-col gap-6">
      { formBuilder.buildQueryError() }
      <div className="grid grid-cols-2 gap-6">
        { formBuilder.buildField({ name: 'Manufacturer', field: 'manufacturer', type: 'manufacturer' }) }
        { formBuilder.buildField({ name: 'Model', field: 'model', type: 'asset-type-model' }) }
      </div>
      { formBuilder.buildField({ name: 'Weight', field: 'weight', type: 'weight' }) }
      { formBuilder.buildField({ name: 'Stock count', field: 'stockCount', type: 'non-negative-integer' }) }
      { formBuilder.buildField({ name: 'Description', field: 'description', type: 'textarea' }) }
      <div className="grid grid-cols-2 gap-6">
        { formBuilder.buildField({ name: 'Replacement cost', field: 'costCents', type: 'currency' }) }
        { formBuilder.buildField({ name: 'Base rate', field: 'rateCents', type: 'currency' }) }
      </div>
      {
        currentCompany && currentCompany.vatNumber && formBuilder.buildCustomField({
          name: 'Tax rate',
          field: 'taxRateId',
          input: <TaxRateSelector
            invalid={!!fieldErrors?.taxRateId}
            value={attributes.taxRateId}
            onChange={value => setAttributes({ ...attributes, taxRateId: value?.id || '' })}
          />
        })
      }
      <div className="grid grid-cols-2 gap-6">
        { formBuilder.buildField({ name: 'Display on your website', field: 'displayOnWebsite', type: 'switch' }) }
        { formBuilder.buildField({ name: 'Available to events', field: 'availableToEvents', type: 'switch' }) }
      </div>
      { formBuilder.saveButton(save, mutationLoading, saveSuccess) }
    </div>
  )
}
